// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * The drawing surface. Owns the <canvas>, its sizing (devicePixelRatio-aware),
 * pointer handling, the in-progress "draft" shape, and repainting from the board.
 *
 * It emits high-level intents — a draft while you draw (streamed live to peers),
 * a commit when you finish, an erase when the eraser touches a shape, and your
 * cursor position — and leaves id/Lamport assignment + the CRDT + the network to
 * the caller. Everyone renders the SAME logical board, fitted to their own canvas.
 */
import { Board } from './board.ts';
import { fitBoard, screenToLogical, clampToBoard, paintBoard, paintShape, hitTest, type Fit } from './draw.ts';
import type { Shape, ShapeKind, Tool } from './types.ts';

/** A shape without the fields the caller assigns (author, Lamport stamp). */
export type Draft = Omit<Shape, 'author' | 'lamport'>;

export interface CanvasCallbacks {
  onDraftStream: (draft: Draft) => void;
  onDraftCommit: (draft: Draft) => void;
  onErase: (id: string) => void;
  onCursor: (x: number, y: number) => void;
}

const STREAM_MS = 55; // throttle live-stream ops while drawing
const MIN_STEP = 2.2; // min logical distance between recorded freehand points

let idCounter = 0;
function draftId(): string {
  idCounter += 1;
  return `s-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e6).toString(36)}-${idCounter}`;
}

export class BoardCanvas {
  readonly canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private fit: Fit = { scale: 1, ox: 0, oy: 0 };
  private dpr = Math.min(window.devicePixelRatio || 1, 2);
  private cw = 0;
  private ch = 0;

  private tool: Tool = 'pen';
  private color = '#1f2430';
  private width = 4;

  private drawing = false;
  private draft: Draft | null = null;
  private lastStream = 0;
  private erasedThisDrag = new Set<string>();
  private textInput: HTMLTextAreaElement | null = null;

  constructor(
    private container: HTMLElement,
    private board: Board,
    private cb: CanvasCallbacks,
  ) {
    this.canvas = document.createElement('canvas');
    this.canvas.className = 'board-canvas';
    this.container.appendChild(this.canvas);
    this.ctx = this.canvas.getContext('2d') as CanvasRenderingContext2D;

    const ro = new ResizeObserver(() => this.resize());
    ro.observe(this.container);
    window.addEventListener('resize', this.resize);
    this.resize();
    // Layout can measure 0 on the very first frame (or in a headless viewport);
    // re-measure a few times until the container has a real size, so the canvas
    // never gets stuck at the 1×1 fallback.
    this.settle(0);

    this.canvas.addEventListener('pointerdown', this.onDown);
    this.canvas.addEventListener('pointermove', this.onMove);
    this.canvas.addEventListener('pointerup', this.onUp);
    this.canvas.addEventListener('pointercancel', this.onUp);
    this.canvas.addEventListener('pointerleave', this.onLeave);
  }

  setTool(t: Tool): void {
    if (this.drawing) this.cancelDraft();
    this.commitText();
    this.tool = t;
    this.canvas.dataset.tool = t;
  }
  setColor(c: string): void {
    this.color = c;
  }
  setWidth(w: number): void {
    this.width = w;
  }
  currentFit(): Fit {
    return this.fit;
  }

  /** Re-measure repeatedly until the container reports a real size. */
  private settle(attempt: number): void {
    const rect = this.container.getBoundingClientRect();
    if (rect.width > 1 && rect.height > 1) {
      this.resize();
      return;
    }
    if (attempt >= 30) return;
    setTimeout(() => this.settle(attempt + 1), 100);
  }

  private resize = (): void => {
    const rect = this.container.getBoundingClientRect();
    this.cw = Math.max(1, Math.floor(rect.width));
    this.ch = Math.max(1, Math.floor(rect.height));
    this.dpr = Math.min(window.devicePixelRatio || 1, 2);
    this.canvas.width = Math.floor(this.cw * this.dpr);
    this.canvas.height = Math.floor(this.ch * this.dpr);
    this.canvas.style.width = `${this.cw}px`;
    this.canvas.style.height = `${this.ch}px`;
    this.fit = fitBoard(this.cw, this.ch);
    this.render();
  }

  /** Repaint the whole board plus any in-progress draft. */
  render(): void {
    paintBoard(this.ctx, this.board.visible(), this.cw, this.ch, this.dpr, this.fit);
    if (this.draft && this.draft.points.length >= 2) {
      this.ctx.save();
      this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);
      this.ctx.translate(this.fit.ox, this.fit.oy);
      this.ctx.scale(this.fit.scale, this.fit.scale);
      this.ctx.beginPath();
      this.ctx.rect(0, 0, 1600, 1000);
      this.ctx.clip();
      paintShape(this.ctx, { ...this.draft, author: '', lamport: 0 });
      this.ctx.restore();
    }
  }

  private toLogical(e: PointerEvent): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    const raw = screenToLogical(e.clientX - rect.left, e.clientY - rect.top, this.fit);
    return clampToBoard(raw.x, raw.y);
  }

  private textFontSize(): number {
    return Math.round(22 + this.width * 3.5);
  }

  private onDown = (e: PointerEvent): void => {
    if (e.button !== undefined && e.button !== 0) return;
    const p = this.toLogical(e);

    if (this.tool === 'text') {
      this.openTextInput(p.x, p.y);
      return;
    }

    this.canvas.setPointerCapture(e.pointerId);

    if (this.tool === 'eraser') {
      this.drawing = true;
      this.erasedThisDrag.clear();
      this.eraseAt(p.x, p.y);
      return;
    }

    this.drawing = true;
    const kind = this.tool as ShapeKind;
    this.draft = { id: draftId(), kind, color: this.color, width: this.width, points: [p.x, p.y, p.x, p.y] };
    this.render();
  };

  private onMove = (e: PointerEvent): void => {
    const p = this.toLogical(e);
    this.cb.onCursor(p.x, p.y);
    if (!this.drawing) return;

    if (this.tool === 'eraser') {
      this.eraseAt(p.x, p.y);
      return;
    }
    if (!this.draft) return;

    if (this.draft.kind === 'pen') {
      const pts = this.draft.points;
      const points: Array<{ x: number; y: number }> =
        typeof e.getCoalescedEvents === 'function' ? e.getCoalescedEvents().map((c) => this.toLogical(c)) : [p];
      if (points.length === 0) points.push(p);
      for (const q of points) {
        const lx = pts[pts.length - 2];
        const ly = pts[pts.length - 1];
        if (Math.hypot(q.x - lx, q.y - ly) >= MIN_STEP) pts.push(q.x, q.y);
      }
    } else {
      // line / rect / ellipse / arrow — two-point shapes; move the end point.
      this.draft.points[2] = p.x;
      this.draft.points[3] = p.y;
    }
    this.render();
    this.maybeStream();
  };

  private onUp = (e: PointerEvent): void => {
    if (!this.drawing) return;
    this.drawing = false;
    try {
      this.canvas.releasePointerCapture(e.pointerId);
    } catch {
      /* not captured */
    }
    if (this.tool === 'eraser') {
      this.erasedThisDrag.clear();
      return;
    }
    const d = this.draft;
    this.draft = null;
    if (!d) return;
    if (this.isDegenerate(d)) {
      this.render();
      return;
    }
    this.cb.onDraftCommit(d);
  };

  private onLeave = (): void => {
    // Keep drawing if the pointer is captured (drag past the edge); nothing to do.
  };

  private maybeStream(): void {
    if (!this.draft) return;
    const now = Date.now();
    if (now - this.lastStream < STREAM_MS) return;
    this.lastStream = now;
    if (!this.isDegenerate(this.draft)) this.cb.onDraftStream({ ...this.draft, points: this.draft.points.slice() });
  }

  private isDegenerate(d: Draft): boolean {
    const p = d.points;
    if (d.kind === 'pen') return p.length < 4;
    if (p.length < 4) return true;
    return Math.hypot(p[2] - p[0], p[3] - p[1]) < 3;
  }

  private cancelDraft(): void {
    this.drawing = false;
    this.draft = null;
    this.render();
  }

  private eraseAt(x: number, y: number): void {
    const radius = Math.max(10, this.width * 2.5);
    const id = hitTest(this.board.visible(), x, y, radius);
    if (id && !this.erasedThisDrag.has(id)) {
      this.erasedThisDrag.add(id);
      this.cb.onErase(id);
    }
  }

  // ── text tool ───────────────────────────────────────────────────────────────
  private openTextInput(lx: number, ly: number): void {
    this.commitText();
    const fs = this.textFontSize();
    const ta = document.createElement('textarea');
    ta.className = 'board-text-input';
    ta.rows = 1;
    ta.spellcheck = false;
    ta.style.left = `${this.fit.ox + lx * this.fit.scale}px`;
    ta.style.top = `${this.fit.oy + (ly - fs) * this.fit.scale}px`;
    ta.style.fontSize = `${fs * this.fit.scale}px`;
    ta.style.color = this.color;
    ta.dataset.lx = String(lx);
    ta.dataset.ly = String(ly);
    ta.dataset.fs = String(fs);
    ta.dataset.color = this.color;
    this.container.appendChild(ta);
    this.textInput = ta;
    setTimeout(() => ta.focus(), 0);
    ta.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter' && !ev.shiftKey) {
        ev.preventDefault();
        this.commitText();
      } else if (ev.key === 'Escape') {
        ev.preventDefault();
        this.discardText();
      }
    });
    ta.addEventListener('blur', () => this.commitText());
    ta.addEventListener('input', () => {
      ta.style.height = 'auto';
      ta.style.height = `${ta.scrollHeight}px`;
    });
  }

  private commitText(): void {
    const ta = this.textInput;
    if (!ta) return;
    this.textInput = null;
    const text = ta.value.trim();
    const lx = Number(ta.dataset.lx);
    const ly = Number(ta.dataset.ly);
    const fs = Number(ta.dataset.fs);
    const color = ta.dataset.color || this.color;
    ta.remove();
    if (!text) return;
    this.cb.onDraftCommit({ id: draftId(), kind: 'text', color, width: this.width, points: [lx, ly], text, fontSize: fs });
  }

  private discardText(): void {
    if (this.textInput) {
      this.textInput.remove();
      this.textInput = null;
    }
  }

  destroy(): void {
    this.discardText();
    window.removeEventListener('resize', this.resize);
  }
}
